import {
  IonButton,
  IonButtons,
  IonHeader,
  IonModal,
  IonToolbar,
} from '@ionic/react';
import React, { useContext, useState } from 'react';
import { Redirect } from 'react-router';
import { CONVERSATION_STATUS } from '../../constants';
import {
  ConversationContext,
  PaymentContext,
  TranslationContext,
  UserContext,
} from '../../contexts';
import {
  AuthorType,
  Category,
  Character,
  Customer,
  Expert,
  MessageType,
} from '../../models';
import { conversationResource } from '../../resource';
import { Avatar, CharacterAvatar } from '../global';
import { Close } from '../icons';
import { BalanceInformation } from './BalanceInformation';
import { BalanceCustomerInformation } from './BalanceCustomerInformation';

interface Props {
  character: Character | null;
  send: (
    conversation: string,
    message: string,
    type?: MessageType,
    author?: string,
    authorIdentifier?: string,
    authorType?: string,
    conversationBetween?: object,
  ) => void;
}

export const ChatHeader: React.FC<Props> = ({ character, send }) => {
  const {
    conversation,
    idConversation,
    target,
    status,
    setStatus,
    setConversation,
  } = useContext(ConversationContext);
  const { translate } = useContext(TranslationContext);
  const { isExpert, isCustomer, user } = useContext(UserContext);
  const { rechargeLink } = useContext(PaymentContext);
  const [showGoBackConfirmation, setShowGoBackConfirmation] = useState(false);
  const [showBuyCreditConfirmation, setShowBuyCreditConfirmation] =
    useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const expert = target as Expert;

  if (!target || !conversation) {
    return null;
  }

  const sendLeaveMessage = async () => {
    if (user && isCustomer) {
      send(
        idConversation,
        translate('chat.leave', { name: user.name }),
        MessageType.LEAVE,
        user.name,
        user.authorIdentifier,
        AuthorType.SYSTEM,
        [user.authorIdentifier, expert.authorIdentifier],
      );
    }
  };

  /**
   * Redirect when a user clicks on back button
   * - if expert : redirects to the dashboard
   * - if customer:
   *     - in readonly mode, redirects to past conversation list
   *     - if conversation is stopped, sends a "leave conversation" message to Mercure, then redirects to expert list
   *     - otherwise, display a confirmation popin
   */
  const goBack = () => {
    if (isExpert) {
      setRedirectPath('/expert/dashboard');
    } else {
      if (status === CONVERSATION_STATUS.STOP) {
        finishReading();
      } else {
        setShowGoBackConfirmation(true);
      }
    }
  };

  const confirmGoBack = () => {
    setShowGoBackConfirmation(false);
    finishReading();
  };

  const finishReading = () => {
    const finishConversation = async () =>
      setConversation(await conversationResource.finish(idConversation));

    /* eslint-disable no-console */
    finishConversation()
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        sendLeaveMessage().finally(() => {
          setStatus(CONVERSATION_STATUS.STOP);
          if (isCustomer) {
            if (conversation != null && character !== null) {
              setRedirectPath('/character/' + character.shortCode);
            } else {
              setRedirectPath('/customer/experts');
            }
          } else {
            setRedirectPath('/expert/dashboard');
          }
        });
      });
    /* eslint-enable no-console */
  };

  /**
   * Leaving the chat to top up the account is a one way trip, so ask for a
   * confirmation first, like when clicking on the back button.
   */
  const confirmBuyMoreCredit = () => {
    setShowBuyCreditConfirmation(false);
    window.location.href = rechargeLink;
  };

  const getCategoriesNames = () => {
    if (
      target &&
      (target as Expert).categories &&
      (target as Expert).categories.length
    ) {
      return (target as Expert).categories
        .map((category: Category) => category.name)
        .join(translate('expert.category.separator'));
    }

    return '';
  };

  if (redirectPath) {
    return <Redirect push to={redirectPath} />;
  }

  return (
    <IonHeader data-testid="chat-header-content">
      <IonToolbar className="toolbar-item">
        <IonButtons slot="start">
          <IonButton
            fill="clear"
            onClick={goBack}
            routerDirection="back"
            data-testid="back"
          >
            <Close />
          </IonButton>
          {isCustomer && rechargeLink ? (
            <IonButton
              onClick={() => setShowBuyCreditConfirmation(true)}
              fill="outline"
              className="buy-more-credit"
              data-testid="buy-more-credit"
            >
              {translate('chat.buyMoreCredit') || 'Buy more credit'}
            </IonButton>
          ) : null}
        </IonButtons>
        <IonButtons slot="end" className="user-info" data-testid="target-info">
          <div className="header-meta">
            <div className="header-mood">{translate('chat.target')}</div>
            <div className="header-name">
              {isExpert && status && status !== CONVERSATION_STATUS.STOP ? (
                <IonButton
                  onClick={finishReading}
                  fill="solid"
                  data-testid="finish-conversation"
                >
                  {translate('chat.expertFinishReading')}
                </IonButton>
              ) : null}
              {isCustomer ? (
                <span className="header-expert-category">
                  {getCategoriesNames()}
                </span>
              ) : null}
              {isExpert
                ? target.name
                : character !== null
                  ? character.name.charAt(0).toUpperCase() +
                    character.name.slice(1)
                  : ''}
            </div>
          </div>
          <div className="header-image">
            {isExpert ? (
              <Avatar user={null} />
            ) : (
              <CharacterAvatar
                user={character}
                isOnline={
                  status !== CONVERSATION_STATUS.FINISHED &&
                  status !== CONVERSATION_STATUS.STOP
                }
              />
            )}
          </div>
        </IonButtons>
      </IonToolbar>
      {isExpert ? (
        <BalanceInformation customer={target as Customer} status={status} />
      ) : null}
      {isCustomer ? <BalanceCustomerInformation status={status} /> : null}
      <IonModal
        isOpen={showGoBackConfirmation}
        className="confirmation-modal"
        backdropDismiss={false}
      >
        <div className="container" data-testid="confirmation-modal">
          <p>{translate('chat.finishReadingConfirmation')}</p>
          <p>{translate('chat.finishReadingConfirmationExplain')}</p>
          <div className="controls">
            <IonButton onClick={() => setShowGoBackConfirmation(false)}>
              {translate('chat.resumeReading')}
            </IonButton>
            <IonButton onClick={confirmGoBack}>
              {translate('chat.finishReading')}
            </IonButton>
          </div>
        </div>
      </IonModal>
      <IonModal
        isOpen={showBuyCreditConfirmation}
        className="confirmation-modal"
        backdropDismiss={false}
      >
        <div className="container" data-testid="buy-credit-confirmation-modal">
          <p>{translate('chat.buyMoreCreditConfirmation')}</p>
          <p>{translate('chat.buyMoreCreditConfirmationExplain')}</p>
          <div className="controls">
            <IonButton
              onClick={() => setShowBuyCreditConfirmation(false)}
              data-testid="cancel-buy-more-credit"
            >
              {translate('chat.cancelBuyMoreCredit')}
            </IonButton>
            <IonButton
              onClick={confirmBuyMoreCredit}
              data-testid="confirm-buy-more-credit"
            >
              {translate('chat.confirmBuyMoreCredit')}
            </IonButton>
          </div>
        </div>
      </IonModal>
    </IonHeader>
  );
};
